import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { translations, LOCALES } from './translations'

/**
 * Lightweight i18n: a context carrying { lang, setLang, t, locale }.
 *
 * - t('home.heroTitlePre') resolves a dot path in the active dictionary.
 * - t('pricing.upToMembers', { count: 50 }) interpolates {count}.
 * - Arrays/objects are returned as-is so components can map over them.
 * - Missing keys fall back chosen language → English → Norwegian (the source
 *   of truth), then to the key itself — never a blank string.
 *
 * Initial language: stored choice if any, else the browser's language when we
 * support it, else Norwegian (the platform default).
 */

const STORAGE_KEY = 'cn_lang'
const SUPPORTED = Object.keys(translations)

const LanguageContext = createContext(null)

function resolvePath(dict, path) {
  return path.split('.').reduce((node, part) => (node == null ? undefined : node[part]), dict)
}

function interpolate(text, vars) {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (match, name) => (vars[name] !== undefined ? String(vars[name]) : match))
}

function detectInitialLang() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (SUPPORTED.includes(stored)) return stored
  } catch {
    // Storage unavailable (private mode etc.) — fall through to detection.
  }

  const candidates = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || []
  for (const candidate of candidates) {
    const primary = String(candidate || '').toLowerCase().split('-')[0]
    // Both written forms of Norwegian (and the bare 'no' tag) map to nb.
    const mapped = primary === 'no' || primary === 'nn' ? 'nb' : primary
    if (SUPPORTED.includes(mapped)) return mapped
  }

  return 'nb'
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang)

  // Keep the document language honest for screen readers and search engines.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo(() => {
    const setLang = (next) => {
      if (!SUPPORTED.includes(next)) return
      setLangState(next)
      try {
        window.localStorage.setItem(STORAGE_KEY, next)
      } catch {
        // Non-fatal: the choice just won't persist.
      }
    }

    const t = (key, vars) => {
      let valueForKey = resolvePath(translations[lang], key)
      if (valueForKey === undefined && lang !== 'en') valueForKey = resolvePath(translations.en, key)
      if (valueForKey === undefined) valueForKey = resolvePath(translations.nb, key)
      if (valueForKey === undefined) return key
      return typeof valueForKey === 'string' ? interpolate(valueForKey, vars) : valueForKey
    }

    return { lang, setLang, t, locale: LOCALES[lang] || 'nb-NO' }
  }, [lang])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLang() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLang must be used inside <LanguageProvider>')
  return ctx
}
